import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import PricingModal from "@/components/subscription/PricingModal";
import { useSubscription } from "@/hooks/useSubscription";

const WelcomeVideoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTabletOrMobile = useIsTabletOrMobile();
  const isMobile = useIsMobile();
  const [showPricing, setShowPricing] = useState(false);

  // Use consolidated subscription hook
  const { subscription, loading } = useSubscription();

  // Redirect to login if not authenticated
  if (!user) {
    navigate("/login");
    return null;
  }

  const handleVideoWatched = () => {
    setShowPricing(true);
  };

  const handlePricingComplete = () => {
    setShowPricing(false);
    navigate("/");
  };

  if (isTabletOrMobile) {
    return (
      <>
        <div className="min-h-screen flex flex-col bg-app-background">
          {/* Top Navigation */}
          <nav className="w-full px-4 h-[58px] flex items-center">
            <div className="flex justify-start">
              <img src="/lovable-uploads/72a43dd6-4d4e-41ee-a66d-387b3974ffb8.png" alt="Studibudi Logo" className="h-6 w-auto" />
            </div>
          </nav>
          
          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="w-full max-w-2xl">
              <div style={!isMobile ? {
                backdropFilter: 'blur(40px)'
              } : {
                backdropFilter: 'blur(40px)'
              }} className="backdrop-blur-3xl rounded-2xl p-6">
                {/* Welcome Content */}
                <div className="space-y-6">
                  <div className="text-center">
                    <h1 className="text-2xl font-medium text-gray-900 mb-4">Welcome to Studibudi!</h1>
                    <p className="text-gray-600 mb-6">
                      Watch this quick video to get started with your learning journey.
                    </p>
                  </div>

                  {/* Video Container */}
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-6">
                    <iframe 
                      src="https://dancing-marmot.clueso.site/embed/fad26188-6a0d-4148-83f2-0c0cf1b5ea6a" 
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0"
                      allowFullScreen
                      allow="autoplay; fullscreen"
                    />
                  </div>

                  {/* Action Button */}
                  <div className="text-center">
                    <Button
                      onClick={handleVideoWatched}
                      className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none"
                    >
                      I watched the video
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <PricingModal 
          open={showPricing} 
          onOpenChange={setShowPricing}
          isTrialMode={true}
          onTrialComplete={handlePricingComplete}
        />
      </>
    );
  }

  // Desktop layout
  return (
    <>
      <div className="min-h-screen flex flex-col bg-app-background">
        {/* Top Navigation */}
        <nav className="w-full px-4 h-[58px] flex items-center">
          <div className="flex justify-start">
            <img src="/lovable-uploads/72a43dd6-4d4e-41ee-a66d-387b3974ffb8.png" alt="Studibudi Logo" className="h-6 w-auto" />
          </div>
        </nav>
        
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-[80px]">
          <div className="flex-1 flex items-center justify-center">
            <div style={{
              backdropFilter: 'blur(40px)'
            }} className="w-full backdrop-blur-3xl max-w-3xl rounded-2xl p-8">
              {/* Welcome Content */}
              <div className="space-y-8">
                <div className="text-center">
                  <h1 className="text-3xl font-medium text-gray-900 mb-4">Welcome to Studibudi!</h1>
                  <p className="text-gray-600 mb-8 text-lg">
                    Watch this quick video to get started with your learning journey.
                  </p>
                </div>

                {/* Video Container */}
                <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-8">
                  <iframe 
                    src="https://dancing-marmot.clueso.site/embed/fad26188-6a0d-4148-83f2-0c0cf1b5ea6a" 
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay; fullscreen"
                  />
                </div>

                {/* Action Button */}
                <div className="text-center">
                  <Button
                    onClick={handleVideoWatched}
                    className="w-full max-w-md h-12 bg-black hover:bg-gray-800 text-white font-medium rounded-2xl border-0 shadow-none"
                  >
                    I watched the video
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PricingModal 
        open={showPricing} 
        onOpenChange={setShowPricing}
        isTrialMode={true}
        onTrialComplete={handlePricingComplete}
      />
    </>
  );
};

export default WelcomeVideoPage;